import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from './api';
import { useAuth } from './useAuth';
import { getAircraftModex } from './aircraftModex';

const normalizeModex = (modex) => {
  if (!modex) {
    return '';
  }
  const trimmed = String(modex).trim();
  if (!trimmed || trimmed === '-' || trimmed.toLowerCase() === 'n/a') {
    return '';
  }
  return trimmed;
};

const normalizeModexForMatch = (modex) => {
  const normalized = normalizeModex(modex);
  if (!normalized) {
    return '';
  }
  return String(normalized).replace(/^0+/, '') || '0';
};

const parseBayId = (bayId) => {
  if (!bayId) {
    return { prefix: '', number: Number.POSITIVE_INFINITY };
  }
  const match = String(bayId).trim().match(/^([A-Za-z]+)(\d+)$/);
  if (!match) {
    return { prefix: String(bayId).trim(), number: Number.POSITIVE_INFINITY };
  }
  return {
    prefix: match[1].toUpperCase(),
    number: Number(match[2])
  };
};

const HangarBayLocationsTable = ({ theme }) => {
  const [hangarSpots, setHangarSpots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedSite, setSelectedSite] = useState('');
  const [selectedBayLocation, setSelectedBayLocation] = useState('');
  const [selectedValidity, setSelectedValidity] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [lastUpdatedAt, setLastUpdatedAt] = useState(null);
  const [aircraftStatusMap, setAircraftStatusMap] = useState({});
  const { isStaff, groups } = useAuth();
  const canViewRestricted = isStaff || groups.includes('Supervisor');

  const statusColors = {
    UP: '#28a745',
    XC: '#20c997',
    DN: '#dc3545',
    DS: '#dc3545',
    PM: '#ffc107',
    FCF: '#8716bbff',
    AWP: '#335d80ff',
    OTH: '#6c757d',
    DET: '#6c757d',
    AIMD: '#6c757d',
    ACI: '#6c757d',
    REI: '#6c757d'
  };

  const fetchHangarSpots = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await api.get('/aircraft-maintenance-hangar-spots/');
      let data = response.data;
      if (response.data && response.data.results) {
        data = response.data.results;
      }
      setHangarSpots(Array.isArray(data) ? data : []);
      setLastUpdatedAt(new Date());
    } catch (err) {
      console.error('Error fetching hangar spots:', err);
      setError('Unable to load hangar spot data.');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchAircraftStatus = useCallback(async () => {
    try {
      const response = await api.get('/aircraft-status/');
      let data = response.data;
      if (response.data && response.data.results) {
        data = response.data.results;
      }
      const statusMap = {};
      if (Array.isArray(data)) {
        data.forEach((aircraft) => {
          if (aircraft && aircraft.buno) {
            const modex = normalizeModexForMatch(getAircraftModex(aircraft));
            if (modex) {
              statusMap[modex] = {
                status: aircraft.status || '',
                reason: aircraft.reason || '',
                internal_comments: aircraft.internal_comments || '',
                buno: aircraft.buno
              };
            }
          }
        });
      }
      setAircraftStatusMap(statusMap);
    } catch (err) {
      console.error('Error fetching aircraft status:', err);
    }
  }, []);

  useEffect(() => {
    fetchHangarSpots();
    fetchAircraftStatus();
    const interval = setInterval(() => {
      fetchHangarSpots();
      fetchAircraftStatus();
    }, 10000);
    return () => clearInterval(interval);
  }, [fetchHangarSpots, fetchAircraftStatus]);

  const availableSites = useMemo(() => {
    const sites = [...new Set(hangarSpots.map((spot) => spot.site).filter(Boolean))];
    return sites.sort();
  }, [hangarSpots]);

  const filteredSites = useMemo(() => {
    if (isStaff) {
      return availableSites;
    }
    return availableSites.filter((site) => groups.includes(site));
  }, [availableSites, groups, isStaff]);

  useEffect(() => {
    if (!isStaff && groups.length > 0 && !selectedSite) {
      const siteGroups = groups.filter((group) => availableSites.includes(group));
      if (siteGroups.length === 1) {
        setSelectedSite(siteGroups[0]);
      }
    }
  }, [availableSites, groups, isStaff, selectedSite]);

  const availableBayLocations = useMemo(() => {
    const locations = [...new Set(hangarSpots.map((spot) => spot.bay_location).filter(Boolean))];
    return locations.sort();
  }, [hangarSpots]);

  const filteredSpots = useMemo(() => {
    let spots = hangarSpots;

    if (selectedSite) {
      spots = spots.filter((spot) => spot.site === selectedSite);
    } else if (!isStaff && filteredSites.length > 0) {
      spots = spots.filter((spot) => filteredSites.includes(spot.site));
    }

    if (selectedBayLocation) {
      spots = spots.filter((spot) => spot.bay_location === selectedBayLocation);
    }

    if (selectedValidity) {
      const isValid = selectedValidity === 'valid';
      spots = spots.filter((spot) => Boolean(spot.is_valid) === isValid);
    }

    if (searchTerm.trim()) {
      const term = searchTerm.trim().toLowerCase();
      spots = spots.filter((spot) => {
        const bayId = String(spot.bay_id || '').toLowerCase();
        const modex = normalizeModex(spot.aircraft_modex).toLowerCase();
        return bayId.includes(term) || modex.includes(term);
      });
    }

    return spots.slice().sort((a, b) => {
      const siteA = a.site || '';
      const siteB = b.site || '';
      if (siteA !== siteB) {
        return siteA.localeCompare(siteB);
      }

      const locationA = a.bay_location || '';
      const locationB = b.bay_location || '';
      if (locationA !== locationB) {
        return locationA.localeCompare(locationB);
      }

      const parsedA = parseBayId(a.bay_id);
      const parsedB = parseBayId(b.bay_id);
      if (parsedA.prefix !== parsedB.prefix) {
        return parsedA.prefix.localeCompare(parsedB.prefix);
      }
      if (parsedA.number !== parsedB.number) {
        return parsedA.number - parsedB.number;
      }
      return String(a.bay_id || '').localeCompare(String(b.bay_id || ''));
    });
  }, [
    hangarSpots,
    selectedSite,
    selectedBayLocation,
    selectedValidity,
    searchTerm,
    isStaff,
    filteredSites
  ]);

  const summary = useMemo(() => {
    const total = filteredSpots.length;
    const assigned = filteredSpots.filter((spot) => normalizeModex(spot.aircraft_modex)).length;
    const valid = filteredSpots.filter((spot) => Boolean(spot.is_valid)).length;
    return { total, assigned, valid };
  }, [filteredSpots]);

  const tableColumns = useMemo(() => ([
    { key: 'bay_id', label: 'Bay ID' },
    { key: 'bay_location', label: 'Hangar' },
    { key: 'site', label: 'Site' },
    { key: 'aircraft_modex', label: 'Aircraft Modex' },
    { key: 'status', label: 'Status' },
    { key: 'reason', label: 'Reason/Remarks' },
    { key: 'internal_comments', label: 'Internal Comments', hidden: !canViewRestricted }
  ]).filter((column) => !column.hidden), [canViewRestricted]);

  return (
    <div style={{
      padding: '20px',
      height: '100%',
      overflow: 'auto',
      backgroundColor: theme === 'dark' ? '#121212' : '#f8f9fa'
    }}>
      <div style={{
        marginBottom: '20px',
        display: 'flex',
        alignItems: 'center',
        gap: '15px',
        flexWrap: 'wrap'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <label style={{
            fontSize: '14px',
            fontWeight: 'bold',
            color: theme === 'dark' ? '#e2e8f0' : '#2d3748'
          }}>
            Filter by Site:
          </label>
          <select
            value={selectedSite}
            onChange={(event) => setSelectedSite(event.target.value)}
            style={{
              padding: '8px 12px',
              borderRadius: '6px',
              border: `1px solid ${theme === 'dark' ? '#4a5568' : '#cbd5e0'}`,
              background: theme === 'dark' ? '#2d3748' : 'white',
              color: theme === 'dark' ? '#e2e8f0' : '#2d3748',
              fontSize: '14px',
              cursor: 'pointer',
              minWidth: '150px'
            }}
          >
            <option value="">All Sites</option>
            {filteredSites.map((site) => (
              <option key={site} value={site}>{site}</option>
            ))}
          </select>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <label style={{
            fontSize: '14px',
            fontWeight: 'bold',
            color: theme === 'dark' ? '#e2e8f0' : '#2d3748'
          }}>
            Hangar:
          </label>
          <select
            value={selectedBayLocation}
            onChange={(event) => setSelectedBayLocation(event.target.value)}
            style={{
              padding: '8px 12px',
              borderRadius: '6px',
              border: `1px solid ${theme === 'dark' ? '#4a5568' : '#cbd5e0'}`,
              background: theme === 'dark' ? '#2d3748' : 'white',
              color: theme === 'dark' ? '#e2e8f0' : '#2d3748',
              fontSize: '14px',
              cursor: 'pointer',
              minWidth: '170px'
            }}
          >
            <option value="">All Hangars</option>
            {availableBayLocations.map((location) => (
              <option key={location} value={location}>{location}</option>
            ))}
          </select>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <label style={{
            fontSize: '14px',
            fontWeight: 'bold',
            color: theme === 'dark' ? '#e2e8f0' : '#2d3748'
          }}>
            Validity:
          </label>
          <select
            value={selectedValidity}
            onChange={(event) => setSelectedValidity(event.target.value)}
            style={{
              padding: '8px 12px',
              borderRadius: '6px',
              border: `1px solid ${theme === 'dark' ? '#4a5568' : '#cbd5e0'}`,
              background: theme === 'dark' ? '#2d3748' : 'white',
              color: theme === 'dark' ? '#e2e8f0' : '#2d3748',
              fontSize: '14px',
              cursor: 'pointer',
              minWidth: '140px'
            }}
          >
            <option value="">All</option>
            <option value="valid">Valid</option>
            <option value="invalid">Invalid</option>
          </select>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <label style={{
            fontSize: '14px',
            fontWeight: 'bold',
            color: theme === 'dark' ? '#e2e8f0' : '#2d3748'
          }}>
            Search:
          </label>
          <input
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            placeholder="Bay ID or Modex"
            style={{
              padding: '8px 12px',
              borderRadius: '6px',
              border: `1px solid ${theme === 'dark' ? '#4a5568' : '#cbd5e0'}`,
              background: theme === 'dark' ? '#2d3748' : 'white',
              color: theme === 'dark' ? '#e2e8f0' : '#2d3748',
              fontSize: '14px',
              minWidth: '170px'
            }}
          />
        </div>

        {(loading || error) && (
          <span style={{ color: theme === 'dark' ? '#a0aec0' : '#718096', fontSize: '14px' }}>
            {loading ? 'Loading...' : error}
          </span>
        )}
      </div>

      <div style={{
        backgroundColor: theme === 'dark' ? '#1a1a1a' : 'white',
        borderRadius: '8px',
        overflow: 'hidden',
        border: `1px solid ${theme === 'dark' ? '#333' : '#e2e8f0'}`
      }}>
        <table style={{
          width: '100%',
          borderCollapse: 'collapse'
        }}>
          <thead>
            <tr style={{
              backgroundColor: theme === 'dark' ? '#2d3748' : '#f7fafc',
              borderBottom: `2px solid ${theme === 'dark' ? '#4a5568' : '#e2e8f0'}`
            }}>
              {tableColumns.map((header, index) => (
                <th
                  key={header.key}
                  style={{
                    padding: '12px 16px',
                    textAlign: 'left',
                    fontWeight: 'bold',
                    fontSize: '14px',
                    color: theme === 'dark' ? '#e2e8f0' : '#2d3748',
                    borderRight: index < tableColumns.length - 1 ? `1px solid ${theme === 'dark' ? '#4a5568' : '#e2e8f0'}` : 'none'
                  }}
                >
                  {header.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filteredSpots.length === 0 ? (
              <tr>
                <td colSpan={tableColumns.length} style={{
                  padding: '40px',
                  textAlign: 'center',
                  color: theme === 'dark' ? '#a0aec0' : '#718096',
                  fontSize: '14px'
                }}>
                  No hangar bay data found
                </td>
              </tr>
            ) : (
              filteredSpots.map((spot, index) => {
                const modex = normalizeModex(spot.aircraft_modex);
                const modexMatch = normalizeModexForMatch(spot.aircraft_modex);
                const statusInfo = modexMatch ? aircraftStatusMap[modexMatch] : null;

                return (
                  <tr
                    key={`${spot.site}_${spot.bay_location}_${spot.bay_id}`}
                    style={{
                      backgroundColor: index % 2 === 0
                        ? (theme === 'dark' ? '#1a1a1a' : 'white')
                        : (theme === 'dark' ? '#2d3748' : '#f7fafc'),
                      borderBottom: `1px solid ${theme === 'dark' ? '#333' : '#e2e8f0'}`,
                      transition: 'background-color 0.2s'
                    }}
                    onMouseEnter={(event) => {
                      event.currentTarget.style.backgroundColor = theme === 'dark' ? '#4a5568' : '#edf2f7';
                    }}
                    onMouseLeave={(event) => {
                      event.currentTarget.style.backgroundColor = index % 2 === 0
                        ? (theme === 'dark' ? '#1a1a1a' : 'white')
                        : (theme === 'dark' ? '#2d3748' : '#f7fafc');
                    }}
                  >
                    <td style={{
                      padding: '12px 16px',
                      fontSize: '14px',
                      color: theme === 'dark' ? '#e2e8f0' : '#2d3748',
                      fontWeight: '600',
                      borderRight: `1px solid ${theme === 'dark' ? '#333' : '#e2e8f0'}`
                    }}>
                      {spot.bay_id || '-'}
                    </td>
                    <td style={{
                      padding: '12px 16px',
                      fontSize: '14px',
                      color: theme === 'dark' ? '#e2e8f0' : '#2d3748',
                      borderRight: `1px solid ${theme === 'dark' ? '#333' : '#e2e8f0'}`
                    }}>
                      {spot.bay_location || '-'}
                    </td>
                    <td style={{
                      padding: '12px 16px',
                      fontSize: '14px',
                      color: theme === 'dark' ? '#e2e8f0' : '#2d3748',
                      fontWeight: '500',
                      borderRight: `1px solid ${theme === 'dark' ? '#333' : '#e2e8f0'}`
                    }}>
                      {spot.site || '-'}
                    </td>
                    <td style={{
                      padding: '12px 16px',
                      fontSize: '14px',
                      color: modex
                        ? (theme === 'dark' ? '#4ade80' : '#28a745')
                        : (theme === 'dark' ? '#a0aec0' : '#718096'),
                      fontWeight: modex ? '600' : 'normal',
                      borderRight: `1px solid ${theme === 'dark' ? '#333' : '#e2e8f0'}`
                    }}>
                      {modex || 'Unassigned'}
                    </td>
                    <td style={{
                      padding: '12px 16px',
                      fontSize: '14px',
                      color: theme === 'dark' ? '#e2e8f0' : '#2d3748'
                    }}>
                      {statusInfo?.status ? (
                        <span style={{
                          display: 'inline-block',
                          padding: '4px 10px',
                          borderRadius: '12px',
                          backgroundColor: statusColors[statusInfo.status] || '#6c757d',
                          color: 'white',
                          fontWeight: 'bold',
                          fontSize: '12px'
                        }}>
                          {statusInfo.status}
                        </span>
                      ) : (
                        <span style={{ color: theme === 'dark' ? '#a0aec0' : '#718096' }}>-</span>
                      )}
                    </td>
                    <td style={{
                      padding: '12px 16px',
                      fontSize: '14px',
                      color: theme === 'dark' ? '#e2e8f0' : '#2d3748',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      maxWidth: '260px'
                    }}>
                      {statusInfo?.reason || '-'}
                    </td>
                    {canViewRestricted && (
                      <td style={{
                        padding: '12px 16px',
                        fontSize: '14px',
                        color: theme === 'dark' ? '#e2e8f0' : '#2d3748',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        maxWidth: '260px'
                      }}>
                        {statusInfo?.internal_comments || '-'}
                      </td>
                    )}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <div style={{
        marginTop: '20px',
        padding: '12px 16px',
        backgroundColor: theme === 'dark' ? '#2d3748' : '#f7fafc',
        borderRadius: '6px',
        fontSize: '14px',
        color: theme === 'dark' ? '#a0aec0' : '#718096',
        display: 'flex',
        flexWrap: 'wrap',
        gap: '16px'
      }}>
        <span>Showing {summary.total} bay{summary.total !== 1 ? 's' : ''}</span>
        <span>{summary.assigned} assigned</span>
        <span>{summary.valid} valid</span>
        {lastUpdatedAt && (
          <span>Last updated {lastUpdatedAt.toLocaleTimeString()}</span>
        )}
      </div>
    </div>
  );
};

export default HangarBayLocationsTable;
